import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useAppSelector } from "app/hooks";
import { misc } from "app/themes/colors";
import EmailMenu from "components/email-menu";
import FaxMenu from "components/fax-menu";
import PatientSearch from "components/patient-search";
import SupaDemoButton from "components/supa-demo-button";
import {
  APPOINTMENT_EMAIL_NOTIFICATION_FEATURE,
  FAX_FEATURE_FLAG,
  VERTICAL_SOUTHWESTERN_LIMITATIONS,
  VISUALIZATION_MODAL
} from "constants/feature-flags";
import AiVisualizationGenerator from "features/ai-visualization-generator";
import { selectCurrentUser } from "features/auth/auth-slice";
import { usePatientDrawer } from "features/patients/patient-drawer";
import TasksButton from "features/patients/patient-header/header-tasks/tasks-button.component";
import useIsMobile from "hooks/use-is-mobile.hook";
import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import useUserRoles from "hooks/use-user-roles.hook";

import { MENU } from "../layout-side-menu";
import AccountMenu from "./account-menu";
import CurrentClinicRegionSelector from "./clinic-region-selector/clinic-regions-selector.component";
import MaintenanceBanner from "./maintenance-banner.component";
import NotificationsMenu from "./notifications-menu";
import ReferralsButton from "./referrals/referrals-button.component";

export default function LayoutTopBar() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const flags = useFlags();
  const params = useParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOpen } = usePatientDrawer();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("xl"));
  const { isPrivateMode, handlePrivacyModeChange } = usePatientPrivacy();
  const { isAdminOrBusinessOwner } = useUserRoles();
  const currentUser = useAppSelector(selectCurrentUser);

  const patientId = String(params["*"] ?? "").split("/")[0];

  const currentPage =
    MENU.find(({ to }) => pathname.startsWith(to)) ||
    (pathname.startsWith("/home")
      ? { primary: "Home", supaDemoId: undefined }
      : undefined) ||
    MENU.find(({ isFallbackPath }) => isFallbackPath);

  const isFaxFeatureEnabled = flags[FAX_FEATURE_FLAG];
  const verticalSouthwesternLimitationsEnabled =
    flags[VERTICAL_SOUTHWESTERN_LIMITATIONS];
  const isEmailNotificationEnabled =
    flags[APPOINTMENT_EMAIL_NOTIFICATION_FEATURE];
  const isVisualizationModalEnabled = flags[VISUALIZATION_MODAL];

  const navigateToAdministrationPage = useCallback(
    () => navigate("/administration"),
    [navigate]
  );

  const [mobileAnchorEl, setMobileAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);

  const handleMobileMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setMobileAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileAnchorEl(null);
  };

  const openMobileMenu = Boolean(mobileAnchorEl);
  const mobileId = openMobileMenu ? "mobile-popover" : undefined;

  const createNewPatientButton = useMemo(() => {
    return (
      <Button
        data-testid="new-patient-button"
        variant="outlined"
        size="small"
        sx={{
          minWidth: "108px",
          width: "245px",
          borderRadius: 2,
          height: 32,
          textTransform: "none",
          fontSize: 14,
          p: 0
        }}
        onClick={() => navigate("/patients/new")}
      >
        <AddIcon fontSize="small" />
        New&nbsp;Patient
      </Button>
    );
  }, [navigate]);

  const menuItemsPart1 = useMemo(() => {
    return (
      <>
        <ReferralsButton />

        <FormControlLabel
          control={
            <Switch
              checked={isPrivateMode}
              onChange={handlePrivacyModeChange}
            />
          }
          label="Privacy Mode"
        />

        {isVisualizationModalEnabled && <AiVisualizationGenerator />}
      </>
    );
  }, [handlePrivacyModeChange, isPrivateMode, isVisualizationModalEnabled]);

  const menuItemsPart2 = useMemo(() => {
    return (
      <>
        <CurrentClinicRegionSelector isLargeScreen={isLargeScreen} />

        {isFaxFeatureEnabled &&
          !(
            verticalSouthwesternLimitationsEnabled && !isAdminOrBusinessOwner
          ) && <FaxMenu />}
        {isEmailNotificationEnabled && (
          <EmailMenu patientId={Number(patientId)} />
        )}
      </>
    );
  }, [
    isAdminOrBusinessOwner,
    isEmailNotificationEnabled,
    isFaxFeatureEnabled,
    isLargeScreen,
    patientId,
    verticalSouthwesternLimitationsEnabled
  ]);

  return (
    <Box
      id="layout-top-bar"
      data-testid="layout-top-bar"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        gap: 2,
        boxSizing: "border-box",
        border: `1px solid ${theme.palette.divider}`,
        borderRight: "none",
        pl: 2,
        pr: 1
      }}
    >
      <Stack rowGap={0.5}>
        <Typography
          variant="h1"
          fontWeight="fontWeightBold"
          color="textPrimary"
          data-testid="page-title"
          sx={{
            fontSize: isMobile ? "20px" : "32px",
            lineHeight: 1.1,
            fontWeight: isMobile ? 800 : undefined,
            pl: isMobile ? 4.5 : undefined
          }}
        >
          {currentPage?.primary}
        </Typography>
        {currentPage?.supaDemoId && (
          <SupaDemoButton supaDemoId={currentPage?.supaDemoId} />
        )}
      </Stack>

      {!isMobile && currentUser?.has_access_to_admin_panel && (
        <Button
          variant="outlined"
          size="small"
          onClick={navigateToAdministrationPage}
        >
          Administration
        </Button>
      )}

      <MaintenanceBanner />
      <Stack
        width="100%"
        display="flex"
        alignItems={isMobile ? "end" : "center"}
        direction={isMobile ? "column" : "row"}
        justifyContent={isMobile ? "flex-end" : "space-between"}
        gap={isLargeScreen ? 2 : 1}
        maxWidth="84%"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          gap={isLargeScreen ? 2 : 1}
          sx={{
            width: "100%"
          }}
        >
          {!isMobile && (
            <PatientSearch showRecent showLengthLimitHint minWidth={375} />
          )}

          {!isMobile && createNewPatientButton}

          <Stack
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={isLargeScreen ? 1 : 0}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              "& label": { mr: 0 },
              mr: isMobile ? -2 : 0
            }}
          >
            {isMobile ? (
              <Button
                size="small"
                sx={{
                  background: misc.sunoGreen,
                  color: "white",
                  width: "28px",
                  minWidth: "28px",
                  height: "28px",
                  mr: 1
                }}
                style={{
                  background: misc.sunoGreen,
                  color: "white"
                }}
                onClick={handleMobileMenuClick}
              >
                <KeyboardArrowDownIcon
                  sx={{
                    transform: openMobileMenu
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s ease-in-out"
                  }}
                />
              </Button>
            ) : null}
            <Popover
              id={mobileId}
              open={openMobileMenu}
              anchorEl={mobileAnchorEl}
              onClose={handleMobileMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left"
              }}
              sx={{ mt: 1 }}
            >
              <Stack
                display="flex"
                alignItems="flex-start"
                width="100%"
                pt={2}
                pb={1}
                pl={3}
                pr={2}
                sx={{
                  background: grey[50],
                  border: `solid 1px ${grey[300]}`
                }}
              >
                <PatientSearch
                  showRecent
                  showLengthLimitHint
                  minWidth={280}
                  sx={{
                    width: "280px",
                    height: "32px",
                    mb: 2
                  }}
                />
                {currentUser?.has_access_to_admin_panel && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={navigateToAdministrationPage}
                    sx={{ mb: 2 }}
                  >
                    Administration
                  </Button>
                )}
                {createNewPatientButton}
                {menuItemsPart1}
                <CurrentClinicRegionSelector isLargeScreen={isLargeScreen} />

                {isFaxFeatureEnabled &&
                  !(
                    verticalSouthwesternLimitationsEnabled &&
                    !isAdminOrBusinessOwner
                  ) && <FaxMenu />}
                {isEmailNotificationEnabled && (
                  <EmailMenu patientId={Number(patientId)} />
                )}
              </Stack>
            </Popover>

            {!isMobile && menuItemsPart1}

            {!isOpen && (
              <Box sx={{ px: isMobile ? 0 : 1 }}>
                <TasksButton />
              </Box>
            )}

            {!isMobile && menuItemsPart2}

            <NotificationsMenu />
            <AccountMenu />
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}
